import type { FC } from 'react'
import type { BulletinSettings, BulletinTemplate, Church, OccurrenceWithEvent } from '@fellowship/shared'
import { getEventType } from '../ui/eventTypes'
import { Wordmark } from '../ui/Wordmark'

// Props shared by every bulletin template. The page slices occurrences to
// settings.maxEvents before passing them, so templates render what they receive.
export interface BulletinRenderProps {
  church: Church
  occurrences: OccurrenceWithEvent[]
  qrDataUrl: string | null
  profileUrl: string
  settings: BulletinSettings
}

// ─── Shared formatting helpers ────────────────────────────────────────────────

function eventTitle(occ: OccurrenceWithEvent): string {
  return occ.overrideTitle ?? occ.event.title
}

function formatTime(iso: string): string {
  return new Date(iso)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .replace(/\s/g, '')
    .toLowerCase()
}

// "Tue · Jun 9"
function formatDayLabel(iso: string): string {
  const d = new Date(iso)
  const wd = d.toLocaleDateString('en-US', { weekday: 'short' })
  const md = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  return `${wd} · ${md}`
}

// "June 6 – June 13, 2026" — spans the shown events, or the current week if none.
function weekRange(occurrences: OccurrenceWithEvent[]): string {
  let start: Date
  let end: Date
  if (occurrences.length > 0) {
    start = new Date(occurrences[0].startsAt)
    end = new Date(occurrences[occurrences.length - 1].startsAt)
  } else {
    start = new Date()
    end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000)
  }
  const s = start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  const e = end.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
  return `${s} – ${e}, ${end.getFullYear()}`
}

function timeAndPlace(occ: OccurrenceWithEvent): string {
  const place = occ.event.locationName
  const time = formatTime(occ.startsAt)
  return place ? `${time} · ${place}` : time
}

// ─── Classic — letter portrait, black-on-white, print-first ───────────────────

const ClassicBulletin: FC<BulletinRenderProps> = ({ church, occurrences, qrDataUrl, settings }) => {
  const serviceTimes = church.serviceTimes ?? []
  const showServiceTimes = settings.showServiceTimes && serviceTimes.length > 0
  const label = {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    color: '#333',
    marginBottom: '10px',
  }

  return (
    <div
      className="bulletin-paper"
      style={{
        width: '8.5in',
        minHeight: '11in',
        boxSizing: 'border-box',
        background: '#fff',
        border: '1px solid #DADADA',
        boxShadow: '0 20px 60px -10px rgba(0,0,0,0.18)',
        padding: '0.7in 0.85in 0.6in',
        fontFamily: 'Georgia, "Times New Roman", serif',
        color: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Masthead */}
      <div style={{ textAlign: 'center' }}>
        {settings.showLogo && church.logoUrl && (
          <img
            src={church.logoUrl}
            alt=""
            style={{ width: '48px', height: '48px', objectFit: 'contain', margin: '0 auto 10px', filter: 'grayscale(1) contrast(1.05)' }}
          />
        )}
        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#444' }}>
          {church.name}
        </div>
        <h1 style={{ margin: '4px 0 2px', fontSize: '40px', fontWeight: 400, lineHeight: 1.05 }}>This Week</h1>
        <div style={{ fontSize: '13px', color: '#555' }}>{weekRange(occurrences)}</div>
      </div>

      <div style={{ borderTop: '1.5px solid #1a1a1a', margin: '18px 0 20px' }} />

      {/* Service times — columnar */}
      {showServiceTimes && (
        <div style={{ marginBottom: '22px' }}>
          <div style={label}>Service Times</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 48px' }}>
            {serviceTimes.map((st, i) => (
              <div key={i}>
                <div style={{ fontSize: '13px', fontWeight: 700 }}>{st.name}</div>
                {(st.day || st.time) && (
                  <div style={{ fontSize: '12px', color: '#555', marginTop: '1px' }}>
                    {[st.day, st.time].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Events — date left, details right, hairline rows */}
      {settings.showEvents && (
        <div>
          <div style={label}>Events This Week</div>
          {occurrences.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#777', paddingTop: '6px' }}>No events scheduled this week.</div>
          ) : (
            <div>
              {occurrences.map((occ) => (
                <div key={occ.id} style={{ display: 'flex', gap: '16px', alignItems: 'baseline', borderTop: '1px solid #ddd', padding: '11px 0' }}>
                  <div style={{ width: '92px', flexShrink: 0, fontSize: '12px', color: '#555' }}>{formatDayLabel(occ.startsAt)}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, lineHeight: 1.25 }}>{eventTitle(occ)}</div>
                    <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>{timeAndPlace(occ)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Spacer pushes the footer to the bottom of the page */}
      <div style={{ flex: 1, minHeight: '24px' }} />

      {/* Footer — verse bottom-left, QR bottom-right */}
      <div style={{ borderTop: '1px solid #ddd', paddingTop: '16px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px' }}>
        <div style={{ maxWidth: '62%' }}>
          {settings.showVerse && settings.verse && (
            <div style={{ fontSize: '12.5px', fontStyle: 'italic', lineHeight: 1.5, color: '#333' }}>{settings.verse}</div>
          )}
          {settings.showGiving && church.givingUrl && (
            <div style={{ fontSize: '11px', color: '#555', marginTop: settings.showVerse ? '8px' : 0 }}>
              Give online · {church.givingUrl}
            </div>
          )}
        </div>
        {qrDataUrl && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '10px', color: '#555', marginBottom: '4px' }}>RSVP online</div>
            <img src={qrDataUrl} alt="QR code to church page" style={{ width: '84px', height: '84px', filter: 'grayscale(1)' }} />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Modern — half-page styled card (color), good for digital/email ───────────

const ModernBulletin: FC<BulletinRenderProps> = ({ church, occurrences, qrDataUrl, profileUrl, settings }) => {
  const serviceTimes = church.serviceTimes ?? []
  const sectionLabel = {
    fontSize: '9.5px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase' as const,
    color: '#A09070',
  }

  return (
    <div
      className="bulletin-paper"
      style={{
        width: '5.5in',
        boxSizing: 'border-box',
        background: '#fff',
        // Square corners — paper has no rounded edges.
        borderRadius: 0,
        border: '1px solid #E5DDD0',
        boxShadow: '0 20px 60px -10px rgba(60,40,10,0.18)',
        padding: '40px 44px 36px',
        fontFamily: 'Georgia, "Times New Roman", serif',
        color: '#1A1610',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '18px', paddingBottom: '20px', marginBottom: '20px', borderBottom: '1.5px solid #EDE8D8' }}>
        {settings.showLogo &&
          (church.logoUrl ? (
            <img src={church.logoUrl} alt="" style={{ width: '60px', height: '60px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
          ) : (
            <div style={{ width: '60px', height: '60px', borderRadius: '10px', flexShrink: 0, background: '#4F3CC9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', fontWeight: 700, color: '#fff' }}>
              {church.name?.charAt(0) ?? 'C'}
            </div>
          ))}
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, lineHeight: 1.2 }}>{church.name}</h1>
          {(church.city || church.state) && (
            <div style={{ fontSize: '12.5px', marginTop: '2px', color: '#6B5E3E' }}>{[church.city, church.state].filter(Boolean).join(', ')}</div>
          )}
          {church.websiteUrl && <div style={{ fontSize: '11px', marginTop: '2px', color: '#A09070' }}>{church.websiteUrl}</div>}
        </div>
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="QR code to church page" style={{ width: '80px', height: '80px', borderRadius: '6px' }} />
          ) : (
            <div style={{ width: '80px', height: '80px', borderRadius: '6px', background: '#F5F0E6' }} />
          )}
          <div style={{ fontSize: '10px', color: '#A09070', marginTop: '4px' }}>Scan to RSVP</div>
        </div>
      </div>

      {/* Service times */}
      {settings.showServiceTimes && serviceTimes.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ ...sectionLabel, marginBottom: '10px' }}>Service Times</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {serviceTimes.map((st, i) => (
              <div key={i} style={{ padding: '6px 12px', background: '#F7F3EC', borderRadius: '999px', border: '1px solid #EDE8D8' }}>
                <span style={{ fontSize: '12px', fontWeight: 600 }}>{st.name}</span>
                {(st.day || st.time) && (
                  <span style={{ fontSize: '11.5px', color: '#6B5E3E' }}>
                    {st.day ? ` · ${st.day}` : ''}
                    {st.time ? ` · ${st.time}` : ''}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming events */}
      {settings.showEvents && (
        <div style={{ marginBottom: settings.showGiving || settings.showVerse ? '20px' : 0 }}>
          <div style={{ ...sectionLabel, marginBottom: '12px' }}>Upcoming Events</div>
          {occurrences.length === 0 ? (
            <div style={{ fontSize: '12.5px', color: '#A09070' }}>No upcoming events scheduled.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {occurrences.map((occ) => {
                const et = getEventType(occ.event.eventType)
                return (
                  <div key={occ.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span aria-hidden style={{ width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: et.soft, color: et.color }}>
                      <et.icon size={13} strokeWidth={1.9} />
                    </span>
                    <div>
                      <div style={{ fontSize: '13.5px', fontWeight: 600, lineHeight: 1.25 }}>{eventTitle(occ)}</div>
                      <div style={{ fontSize: '11.5px', marginTop: '2px', color: '#6B5E3E' }}>{timeAndPlace(occ)}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Giving */}
      {settings.showGiving && church.givingUrl && (
        <div style={{ marginBottom: settings.showVerse ? '20px' : 0, padding: '10px 14px', background: '#F7F3EC', border: '1px solid #EDE8D8', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#4F3CC9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: '#fff', fontSize: '14px' }}>♥</span>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#A09070', marginBottom: '2px' }}>Give Online</div>
            <div style={{ fontSize: '12px', color: '#4F3CC9' }}>{church.givingUrl}</div>
          </div>
        </div>
      )}

      {/* Verse */}
      {settings.showVerse && settings.verse && (
        <div style={{ padding: '14px 16px', background: '#F7F3EC', borderLeft: '3px solid #4F3CC9' }}>
          <div style={{ fontSize: '12.5px', fontStyle: 'italic', lineHeight: 1.55, color: '#4A3D28' }}>{settings.verse}</div>
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: '20px', paddingTop: '14px', borderTop: '1px solid #EDE8D8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: '10px', color: '#C0B49A' }}>{profileUrl}</div>
        <div style={{ display: 'flex', alignItems: 'center', opacity: 0.45 }}>
          <Wordmark size={13} />
        </div>
      </div>
    </div>
  )
}

// ─── Postcard — compact half-letter landscape, big QR, minimal ────────────────

const PostcardBulletin: FC<BulletinRenderProps> = ({ church, occurrences, qrDataUrl, settings }) => {
  const serviceTimes = church.serviceTimes ?? []
  const events = occurrences.slice(0, 3)

  return (
    <div
      className="bulletin-paper"
      style={{
        width: '8.5in',
        height: '5.5in',
        boxSizing: 'border-box',
        background: '#fff',
        border: '1px solid #DADADA',
        boxShadow: '0 20px 60px -10px rgba(0,0,0,0.18)',
        padding: '0.5in 0.6in',
        fontFamily: 'Georgia, "Times New Roman", serif',
        color: '#1a1a1a',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '24px', borderBottom: '1.5px solid #1a1a1a', paddingBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {settings.showLogo && church.logoUrl && (
            <img src={church.logoUrl} alt="" style={{ width: '50px', height: '50px', objectFit: 'contain', filter: 'grayscale(1)' }} />
          )}
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, lineHeight: 1.15 }}>{church.name}</h1>
            {(church.city || church.state) && (
              <div style={{ fontSize: '12.5px', color: '#555', marginTop: '2px' }}>{[church.city, church.state].filter(Boolean).join(', ')}</div>
            )}
          </div>
        </div>
        {qrDataUrl && (
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <img src={qrDataUrl} alt="QR code to church page" style={{ width: '88px', height: '88px', filter: 'grayscale(1)' }} />
            <div style={{ fontSize: '9.5px', color: '#555', marginTop: '2px' }}>Scan to RSVP</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '36px', flex: 1, paddingTop: '14px', minHeight: 0 }}>
        {/* Service times */}
        {settings.showServiceTimes && serviceTimes.length > 0 && (
          <div style={{ flexShrink: 0, maxWidth: '40%' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#333', marginBottom: '8px' }}>Service Times</div>
            {serviceTimes.map((st, i) => (
              <div key={i} style={{ fontSize: '13px', marginBottom: '5px' }}>
                <span style={{ fontWeight: 700 }}>{st.name}</span>
                {(st.day || st.time) && <span style={{ color: '#555' }}> · {[st.day, st.time].filter(Boolean).join(' · ')}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Events */}
        {settings.showEvents && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#333', marginBottom: '8px' }}>This Week</div>
            {events.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#777' }}>No events scheduled.</div>
            ) : (
              events.map((occ) => (
                <div key={occ.id} style={{ fontSize: '13px', marginBottom: '7px', lineHeight: 1.3 }}>
                  <span style={{ color: '#555' }}>{formatDayLabel(occ.startsAt)} · </span>
                  <span style={{ fontWeight: 700 }}>{eventTitle(occ)}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Footer line */}
      {((settings.showVerse && settings.verse) || (settings.showGiving && church.givingUrl)) && (
        <div style={{ borderTop: '1px solid #ddd', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '20px' }}>
          {settings.showVerse && settings.verse ? (
            <div style={{ fontSize: '11.5px', fontStyle: 'italic', color: '#444', maxWidth: '70%' }}>{settings.verse}</div>
          ) : (
            <span />
          )}
          {settings.showGiving && church.givingUrl && (
            <div style={{ fontSize: '11px', color: '#555', flexShrink: 0 }}>Give · {church.givingUrl}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Display — widescreen, dark, built for a lobby TV or slideshow slide ──────

// Native pixel size of the exported/full-screened slide — 720p, 16:9. Fixed
// px (not "in") because this template is never printed; BulletinPage scales
// this node to fit the preview pane and to fit the screen in full-screen mode.
export const DISPLAY_WIDTH = 1280
export const DISPLAY_HEIGHT = 720

const DisplayBulletin: FC<BulletinRenderProps> = ({ church, occurrences, qrDataUrl, settings }) => {
  const serviceTimes = church.serviceTimes ?? []
  const showServiceTimes = settings.showServiceTimes && serviceTimes.length > 0
  const showFooter = (settings.showVerse && settings.verse) || (settings.showGiving && church.givingUrl)

  return (
    <div
      className="bulletin-paper"
      style={{
        width: `${DISPLAY_WIDTH}px`,
        height: `${DISPLAY_HEIGHT}px`,
        boxSizing: 'border-box',
        background: 'linear-gradient(160deg, #171B26 0%, #0B0E14 100%)',
        color: '#F4F2EC',
        fontFamily: 'Georgia, "Times New Roman", serif',
        padding: '52px 60px 40px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
        {settings.showLogo && church.logoUrl && (
          <img src={church.logoUrl} alt="" style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '10px', flexShrink: 0 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: '34px', fontWeight: 700, lineHeight: 1.1 }}>{church.name}</h1>
          {showServiceTimes && (
            <div style={{ marginTop: '6px', fontSize: '15px', color: '#B8AF9A', display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
              {serviceTimes.map((st, i) => (
                <span key={i}>
                  <span style={{ fontWeight: 700, color: '#F4F2EC' }}>{st.name}</span>
                  {(st.day || st.time) && ` · ${[st.day, st.time].filter(Boolean).join(' · ')}`}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Body — events left, QR right */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '48px', marginTop: '34px' }}>
        {settings.showEvents && (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8C8368', marginBottom: '16px' }}>
              This Week
            </div>
            {occurrences.length === 0 ? (
              <div style={{ fontSize: '16px', color: '#8C8368' }}>No events scheduled this week.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {occurrences.map((occ) => (
                  <div key={occ.id} style={{ display: 'flex', alignItems: 'baseline', gap: '18px' }}>
                    <div style={{ width: '118px', flexShrink: 0, fontSize: '15px', color: '#C9A76B', fontWeight: 700 }}>
                      {formatDayLabel(occ.startsAt)}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '22px', fontWeight: 700, lineHeight: 1.2 }}>{eventTitle(occ)}</div>
                      <div style={{ fontSize: '14.5px', color: '#B8AF9A', marginTop: '2px' }}>{timeAndPlace(occ)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {qrDataUrl && (
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
            <div style={{ background: '#fff', borderRadius: '14px', padding: '14px' }}>
              <img src={qrDataUrl} alt="QR code to church page" style={{ width: '160px', height: '160px', display: 'block' }} />
            </div>
            <div style={{ fontSize: '13px', color: '#B8AF9A', textAlign: 'center' }}>Scan for details &amp; RSVP</div>
          </div>
        )}
      </div>

      {/* Footer — verse / giving */}
      {showFooter && (
        <div style={{ borderTop: '1px solid rgba(244,242,236,0.14)', paddingTop: '18px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px' }}>
          <div style={{ maxWidth: '72%' }}>
            {settings.showVerse && settings.verse && (
              <div style={{ fontSize: '15px', fontStyle: 'italic', lineHeight: 1.5, color: '#D9D2C1' }}>{settings.verse}</div>
            )}
          </div>
          {settings.showGiving && church.givingUrl && (
            <div style={{ fontSize: '13px', color: '#B8AF9A', flexShrink: 0 }}>Give online · {church.givingUrl}</div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Slideshow slide — one event per slide, cycled by SlideshowPlayer ─────────

export interface SlideshowSlideProps {
  church: Church
  occurrence: OccurrenceWithEvent | null
  qrDataUrl: string | null
  showImage: boolean
  showDetails: boolean
  showQr: boolean
  slideIndex: number
  slideCount: number
}

// Same dark widescreen canvas as the Display template, but focused on a
// single event at a time — image/details/QR each independently toggleable
// (churches with a small screen may want just one piece to stay legible).
export const SlideshowSlide: FC<SlideshowSlideProps> = ({
  church,
  occurrence,
  qrDataUrl,
  showImage,
  showDetails,
  showQr,
  slideIndex,
  slideCount,
}) => {
  const hasImage = showImage && !!occurrence?.event.imageUrl
  const piecesShown = [hasImage, showDetails, showQr && !!qrDataUrl].filter(Boolean).length

  return (
    <div
      className="bulletin-paper"
      style={{
        width: `${DISPLAY_WIDTH}px`,
        height: `${DISPLAY_HEIGHT}px`,
        boxSizing: 'border-box',
        background: 'linear-gradient(160deg, #171B26 0%, #0B0E14 100%)',
        color: '#F4F2EC',
        fontFamily: 'Georgia, "Times New Roman", serif',
        padding: '40px 60px 32px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8C8368' }}>
        {church.name}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '56px' }}>
        {!occurrence ? (
          <div style={{ fontSize: '20px', color: '#8C8368' }}>No events scheduled.</div>
        ) : (
          <>
            {hasImage && (
              <img
                src={occurrence.event.imageUrl ?? undefined}
                alt=""
                style={{
                  maxHeight: '100%',
                  maxWidth: piecesShown > 1 ? '46%' : '78%',
                  borderRadius: '20px',
                  objectFit: 'cover',
                  boxShadow: '0 20px 60px -10px rgba(0,0,0,0.5)',
                }}
              />
            )}
            {showDetails && (
              <div style={{ textAlign: hasImage ? 'left' : 'center', maxWidth: '620px' }}>
                <div style={{ fontSize: '17px', fontWeight: 700, color: '#C9A76B', marginBottom: '10px' }}>
                  {formatDayLabel(occurrence.startsAt)}
                </div>
                <div style={{ fontSize: '52px', fontWeight: 700, lineHeight: 1.08 }}>{eventTitle(occurrence)}</div>
                <div style={{ fontSize: '20px', color: '#B8AF9A', marginTop: '14px' }}>{timeAndPlace(occurrence)}</div>
              </div>
            )}
            {showQr && qrDataUrl && (
              <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: '#fff', borderRadius: '14px', padding: '14px' }}>
                  <img src={qrDataUrl} alt="QR code to this event" style={{ width: '180px', height: '180px', display: 'block' }} />
                </div>
                <div style={{ fontSize: '13px', color: '#B8AF9A', textAlign: 'center' }}>Scan for details &amp; RSVP</div>
              </div>
            )}
          </>
        )}
      </div>

      {slideCount > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          {Array.from({ length: slideCount }).map((_, i) => (
            <span
              key={i}
              style={{
                width: i === slideIndex ? '22px' : '7px',
                height: '7px',
                borderRadius: '999px',
                background: i === slideIndex ? '#C9A76B' : 'rgba(244,242,236,0.22)',
                transition: 'width 200ms',
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Registry ─────────────────────────────────────────────────────────────────

export interface TemplateMeta {
  label: string
  blurb: string
  // Which picker group this template belongs to — print templates are meant
  // to be printed/PDF'd; screen templates are exported as an image or shown
  // full-screen and are never printed.
  kind: 'print' | 'screen'
  icon: string
  // Short badge shown on the screen-kind picker card, e.g. "IMAGE / FULL SCREEN".
  badge?: string
  // CSS @page size for printing this template. Unused (but harmless) for
  // screen templates, which are never printed.
  pageSize: string
  // Absent for 'slideshow' — BulletinPage renders SlideshowPlayer for it
  // directly instead of going through the generic single-render Component
  // path, since a slideshow needs its own cycling/timer state.
  Component?: FC<BulletinRenderProps>
}

export const BULLETIN_TEMPLATES: Record<BulletinTemplate, TemplateMeta> = {
  classic: {
    label: 'Classic',
    blurb: 'Letter · black & white',
    kind: 'print',
    icon: 'doc',
    pageSize: '8.5in 11in',
    Component: ClassicBulletin,
  },
  modern: {
    label: 'Modern',
    blurb: 'Half-page · color',
    kind: 'print',
    icon: 'doc',
    pageSize: '5.5in 8.5in',
    Component: ModernBulletin,
  },
  postcard: {
    label: 'Postcard',
    blurb: 'Half-letter · landscape',
    kind: 'print',
    icon: 'doc',
    pageSize: '8.5in 5.5in',
    Component: PostcardBulletin,
  },
  display: {
    label: 'Screen',
    blurb: 'One widescreen slide — download as an image or view full-screen',
    kind: 'screen',
    icon: 'monitor',
    badge: 'IMAGE / FULL SCREEN',
    pageSize: '11in 6.19in',
    Component: DisplayBulletin,
  },
  slideshow: {
    label: 'Slideshow',
    blurb: 'Cycles one event at a time — auto-advance or tap through',
    kind: 'screen',
    icon: 'play',
    badge: 'LIVE / AUTO-ADVANCE',
    pageSize: '11in 6.19in',
  },
}

export const TEMPLATE_ORDER: BulletinTemplate[] = ['classic', 'modern', 'postcard', 'display', 'slideshow']
export const PRINT_TEMPLATE_ORDER: BulletinTemplate[] = TEMPLATE_ORDER.filter((k) => BULLETIN_TEMPLATES[k].kind === 'print')
export const SCREEN_TEMPLATE_ORDER: BulletinTemplate[] = TEMPLATE_ORDER.filter((k) => BULLETIN_TEMPLATES[k].kind === 'screen')
